import Conditions from '../../../../../resources/conditions';
import Outputs from '../../../../../resources/outputs';
import { Responses } from '../../../../../resources/responses';
import ZoneId from '../../../../../resources/zone_id';
import { RaidbossData } from '../../../../../types/data';
import { TriggerSet } from '../../../../../types/trigger';

// TODO: Thaliak Rheognosis: is knockback always from east? can we determine where to go?
// TODO: Thaliak Rheognosis Petrine: determine where rocks are somehow and call them
// TODO: Thaliak Tetraktys: use map effect lines to give safe spots for falling walls
// TODO: Thaliak Heiroglyphika: use map effect lines to call safe spots for rotating green squares
// TODO: Llymlaen Dire Straits: use 8CCD/8CCE to call out left => right sort of thing
// TODO: Llymlaen Serpents' Tide: use 8826/887/8828/8829 line 264 cast locations to call safe spots
// TODO: Llymlaen Navigator's Trident knockback: call where to be knocked back to with Serpents' Tide combo
// TODO: Llymlaen Torrential Tridents: use 881B Landing to call where to start and rotation direction
// TODO: Oschon Trek Shot/Swinging Draw: use 264 cast locations to call safe spots
// TODO: Eulogia Love's Light: use map effect lines to call where to start and which way to rotate
// TODO: Eulogia Heiroglyphika: use map effect lines to call safe spots for rotating green squares
// TODO: Eulogia Solar Fans: do they always jump 4 times, so front/back is safe?
// TODO: Eulogia Climbing Shot: should tell you to knockback to red or blue

export type EulogiaForm = 'left' | 'right' | 'inside' | 'unknown';

const eulogiaFormMap: { [count: string]: EulogiaForm } = {
  // first
  '8A0A': 'left',
  '8A0D': 'right',
  '8A10': 'inside',
  // second
  '8A0B': 'left',
  '8A0E': 'right',
  '8A11': 'inside',
  // third
  '8A0C': 'left',
  '8A0F': 'right',
  '8A12': 'inside',
} as const;

export interface Data extends RaidbossData {
  busterTargets: string[];
  soaringMinuet: boolean;
  eulogiaForms: EulogiaForm[];
}

const triggerSet: TriggerSet<Data> = {
  id: 'Thaleia',
  zoneId: ZoneId.Thaleia,
  timelineFile: 'thaleia.txt',
  initData: () => {
    return {
      busterTargets: [],
      soaringMinuet: false,
      eulogiaForms: [],
    };
  },
  timelineTriggers: [
    {
      id: 'Thaleia Thaliak Rheognosis Knockback',
      regex: /^Rheognosis$/,
      beforeSeconds: 5,
      suppressSeconds: 5,
      response: Responses.knockback(),
    },
  ],
  triggers: [
    {
      id: 'Thaleia Thaliak Katarraktes',
      type: 'StartsUsing',
      netRegex: { id: '88D1', source: 'Thaliak', capture: false },
      response: Responses.bleedAoe(),
    },
    {
      id: 'Thaleia Thaliak Thlipsis',
      type: 'StartsUsing',
      netRegex: { id: '88D8', source: 'Thaliak' },
      response: Responses.stackMarkerOn(),
    },
    {
      id: 'Thaleia Thaliak Left Bank',
      type: 'StartsUsing',
      netRegex: { id: '88D2', source: 'Thaliak', capture: false },
      response: Responses.goRight(),
    },
    {
      id: 'Thaleia Thaliak Right Bank',
      type: 'StartsUsing',
      netRegex: { id: '88D3', source: 'Thaliak', capture: false },
      response: Responses.goLeft(),
    },
    {
      id: 'Thaleia Thaliak Left Bank Hieroglyphika',
      type: 'StartsUsing',
      netRegex: { id: '8C2C', source: 'Thaliak', capture: false },
      delaySeconds: 12,
      response: Responses.goRight(),
    },
    {
      id: 'Thaleia Thaliak Right Bank Hieroglyphika',
      type: 'StartsUsing',
      netRegex: { id: '8C2D', source: 'Thaliak', capture: false },
      delaySeconds: 12,
      response: Responses.goLeft(),
    },
    {
      id: 'Thaleia Thaliak Hydroptosis',
      type: 'StartsUsing',
      netRegex: { id: '88D5', source: 'Thaliak' },
      condition: Conditions.targetIsYou(),
      response: Responses.spread(),
    },
    {
      id: 'Thaleia Thaliak Rhyton',
      type: 'HeadMarker',
      netRegex: { id: '01D7' },
      delaySeconds: (data, matches) => {
        data.busterTargets.push(matches.target);
        return data.busterTargets.length === 3 ? 0 : 0.5;
      },
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          tankCleaveOnYou: Outputs.tankCleaveOnYou,
          tankCleaves: Outputs.tankBusterCleaves,
        };

        if (data.busterTargets.length === 0)
          return;
        if (!data.busterTargets.includes(data.me))
          return { infoText: output.tankCleaves!() };
        if (data.role !== 'tank' && data.job !== 'BLU')
          return { alarmText: output.tankCleaveOnYou!() };
        return { alertText: output.tankCleaveOnYou!() };
      },
      run: (data) => data.busterTargets = [],
    },
    {
      id: 'Thaleia Thaliak Rheognosis',
      type: 'StartsUsing',
      netRegex: { id: '88C4', source: 'Thaliak', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Look for knockback position',
          de: 'Nach Rückstoß Position schauen',
          fr: 'Repérez la zone de poussée',
          ja: 'ノックバック位置へ',
          cn: '寻找击退位置',
          ko: '넉백 위치 찾기',
          tc: '尋找擊退位置',
        },
      },
    },
    {
      id: 'Thaleia Thaliak Rheognosis Petrine',
      type: 'StartsUsing',
      netRegex: { id: '88C5', source: 'Thaliak', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback + Rolling stones',
          de: 'Rückstoß + rollende Steine',
          fr: 'Poussée + Rocher',
          ja: 'ノックバック + 石AOE',
          cn: '击退 + 滚石直线AoE',
          ko: '넉백 + 돌 피하기',
          tc: '擊退 + 滾石直線AoE',
        },
      },
    },
    {
      id: 'Thaleia Thaliak Hieroglyphika',
      type: 'StartsUsing',
      netRegex: { id: '88CF', source: 'Thaliak', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go to rotated safe zone',
          de: 'Geh zum rotierten sicheren Feld',
          fr: 'Allez dans une zone sûre après rotation',
          ja: '安置へ移動', // FIXME
          cn: '去旋转后的安全区',
          ko: '회전한 뒤의 안전 지대로 가기',
          tc: '去旋轉後的安全區',
        },
      },
    },
    {
      id: 'Thaleia Llymlaen Tempest',
      type: 'StartsUsing',
      netRegex: { id: '880B', source: 'Llymlaen', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Thaleia Llymlaen Seafoam Spiral',
      type: 'StartsUsing',
      netRegex: { id: '880D', source: 'Llymlaen', capture: false },
      response: Responses.getUnder(),
    },
    {
      id: 'Thaleia Llymlaen Wind Rose',
      type: 'StartsUsing',
      netRegex: { id: '880C', source: 'Llymlaen', capture: false },
      response: Responses.getOut(),
    },
    {
      id: 'Thaleia Llymlaen Navigator\'s Trident Knockback',
      type: 'StartsUsing',
      netRegex: { id: '8811', source: 'Llymlaen', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback',
          de: 'Rückstoß',
          fr: 'Poussée',
          ja: 'まもなくノックバック',
          cn: '击退',
          ko: '넉백',
          tc: '擊退',
        },
      },
    },
    {
      id: 'Thaleia Llymlaen Surging Wave',
      type: 'StartsUsing',
      netRegex: { id: '8812', source: 'Llymlaen', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Far knockback',
          de: 'Weiter Rückstoß',
          fr: 'Poussée au loin',
          ja: '遠くノックバック',
          cn: '长距离击退',
          ko: '멀리 넉백',
          tc: '長距離擊退',
        },
      },
    },
    {
      id: 'Thaleia Llymlaen Left Strait',
      type: 'StartsUsing',
      netRegex: { id: '8851', source: 'Llymlaen', capture: false },
      response: Responses.goRight(),
    },
    {
      id: 'Thaleia Llymlaen Right Strait',
      type: 'StartsUsing',
      netRegex: { id: '8852', source: 'Llymlaen', capture: false },
      response: Responses.goLeft(),
    },
    {
      id: 'Thaleia Llymlaen Deep Dive',
      type: 'StartsUsing',
      netRegex: { id: ['8819', '8834'], source: 'Llymlaen' },
      response: Responses.stackMarkerOn(),
    },
    {
      id: 'Thaleia Llymlaen Stormwinds',
      type: 'StartsUsing',
      netRegex: { id: '881F', source: 'Llymlaen' },
      condition: Conditions.targetIsYou(),
      response: Responses.spread(),
    },
    {
      id: 'Thaleia Llymlaen Torrential Tridents',
      type: 'StartsUsing',
      netRegex: { id: '881A', source: 'Llymlaen', capture: false },
      durationSeconds: 12,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Last trident => 1st trident',
          de: 'Letzer Dreizack => erster Dreizack',
          fr: 'Dernier trident -> 1er trident',
          ja: '最後の槍 => 1番目の槍へ',
          cn: '最后一个三叉戟 => 第一个三叉戟',
          ko: '마지막 창 => 1번째 창으로',
          tc: '最後一個三叉戟 => 第一個三叉戟',
        },
      },
    },
    {
      id: 'Thaleia Llymlaen Godsbane',
      type: 'StartsUsing',
      netRegex: { id: '8824', source: 'Llymlaen', capture: false },
      response: Responses.bleedAoe(),
    },
    {
      id: 'Thaleia Oschon Sudden Downpour',
      type: 'StartsUsing',
      netRegex: { id: ['8999', '899A'], source: 'Oschon', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Thaleia Oschon Trek Shot',
      type: 'StartsUsing',
      netRegex: { id: '898E', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go to side of the arrow (Boss doesn\'t move)',
          de: 'Geh seitlich des Pfeils (Boss bewegt sich nicht)',
          fr: 'Allez sur les côtés de la flèche (le boss ne bouge pas)',
          ja: '矢印の横へ (ボスは動かない)',
          cn: '去箭头两侧 (Boss不会移动)',
          ko: '화살표 옆으로 (보스는 움직이지 않음)',
          tc: '去箭頭兩側 (Boss不會移動)',
        },
      },
    },
    {
      id: 'Thaleia Oschon Reproduce',
      type: 'StartsUsing',
      netRegex: { id: '8989', source: 'Oschon', capture: false },
      alertText: (data, _matches, output) => {
        if (!data.soaringMinuet)
          return output.one!();
        return output.two!();
      },
      outputStrings: {
        one: {
          en: 'Go to side of the arrow',
          de: 'Geh seitlich des Pfeils',
          fr: 'Allez sur les côtés de la flèche',
          ja: '矢印の横へ',
          cn: '去箭头两侧',
          ko: '화살표 옆으로',
          tc: '去箭頭兩側',
        },
        two: {
          en: 'Corner between two arrows',
          de: 'Ecke zwichen 2 Pfeilen',
          fr: 'Coin entre les 2 flèches',
          ja: '2つの矢印の隅',
          cn: '去两箭头间角落',
          ko: '두 화살표 사이로',
          tc: '去兩箭頭間角落',
        },
      },
    },
    {
      id: 'Thaleia Oschon Flinted Foehn',
      type: 'HeadMarker',
      netRegex: { id: '013C' },
      alertText: (data, matches, output) => {
        if (data.me === matches.target)
          return output.stackMarkerOnYou!();
        return output.stackMarkerOn!({ player: data.party.member(matches.target) });
      },
      outputStrings: {
        stackMarkerOn: {
          en: '6x Stack on ${player}',
          de: '6x Sammeln auf ${player}',
          fr: 'Package x6 sur ${player}',
          cn: '6x 分摊点 ${player}',
          ko: '6x 쉐어: ${player}',
          tc: '6x 分攤點 ${player}',
        },
        stackMarkerOnYou: {
          en: '6x Stack on You',
          de: '6x Sammeln auf Dir',
          fr: 'Package x6 sur Vous',
          cn: '6x 分摊点名',
          ko: '6x 쉐어 대상자',
          tc: '6x 分攤點名',
        },
      },
    },
    {
      id: 'Thaleia Oschon Soaring Minuet',
      type: 'StartsUsing',
      netRegex: { id: ['8D0E', '8994'], source: 'Oschon', capture: false },
      response: Responses.getBehind(),
      run: (data) => data.soaringMinuet = true,
    },
    {
      id: 'Thaleia Oschon The Arrow / Eulogia Sunbeam',
      // 0158 = The Arrow (small)
      // 0158 = Eulogia Sunbeam
      // 01F4 = The Arrow (big)
      type: 'HeadMarker',
      netRegex: { id: ['0158', '01F4'] },
      delaySeconds: (data, matches) => {
        data.busterTargets.push(matches.target);
        return data.busterTargets.length === 3 ? 0 : 0.5;
      },
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          tankCleaveOnYou: Outputs.tankCleaveOnYou,
          tankCleaves: Outputs.tankBusterCleaves,
        };

        if (data.busterTargets.length === 0)
          return;
        if (!data.busterTargets.includes(data.me))
          return { infoText: output.tankCleaves!() };
        if (data.role !== 'tank' && data.job !== 'BLU')
          return { alarmText: output.tankCleaveOnYou!() };
        return { alertText: output.tankCleaveOnYou!() };
      },
      run: (data) => data.busterTargets = [],
    },
    {
      id: 'Thaleia Oschon Climbing Shot',
      type: 'StartsUsing',
      netRegex: { id: ['8990', '8991', '8992', '8993'], source: 'Oschon', capture: false },
      suppressSeconds: 10,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback to safe corner',
          de: 'Rückstoß in die sichere Ecke',
          fr: 'Poussée vers un coin sûr',
          ja: 'AOEがないどころへ + ノックバック',
          cn: '击退到安全角落',
          ko: '안전한 구석으로 넉백되기',
          tc: '擊退到安全角落',
        },
      },
    },
    {
      id: 'Thaleia Oschon Lofty Peaks',
      type: 'StartsUsing',
      netRegex: { id: '89A7', source: 'Oschon', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: '(second phase)',
          de: 'Oschon zweite Phase',
          fr: 'Oshon : deuxième phase',
          ja: 'すぐ大きくなる',
          cn: '(二阶段)',
          ko: '(2페이즈)',
          tc: '(二階段)',
        },
      },
    },
    {
      id: 'Thaleia Oschon Piton Pull NE/SW',
      type: 'StartsUsing',
      netRegex: { id: '89A9', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => {
        return output.text!({ front: output.dirNE!(), back: output.dirSW!() });
      },
      outputStrings: {
        text: {
          en: '${front} / ${back}',
          de: '${front} / ${back}',
          fr: '${front} / ${back}',
          cn: '${front} / ${back}',
          ko: '${front} / ${back}',
          tc: '${front} / ${back}',
        },
        dirNE: Outputs.dirNE,
        dirSW: Outputs.dirSW,
      },
    },
    {
      id: 'Thaleia Oschon Piton Pull NW/SE',
      type: 'StartsUsing',
      netRegex: { id: '89AA', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => {
        return output.text!({ front: output.dirNW!(), back: output.dirSE!() });
      },
      outputStrings: {
        text: {
          en: '${front} / ${back}',
          de: '${front} / ${back}',
          fr: '${front} / ${back}',
          cn: '${front} / ${back}',
          ko: '${front} / ${back}',
          tc: '${front} / ${back}',
        },
        dirNW: Outputs.dirNW,
        dirSE: Outputs.dirSE,
      },
    },
    {
      id: 'Thaleia Oschon Altitude',
      type: 'StartsUsing',
      netRegex: { id: '89AF', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go to safe zone',
          de: 'Geh in den sicheren Bereich',
          fr: 'Allez dans une zone sûre',
          ja: '安置で待機',
          cn: '去安全区',
          ko: '안전지대로',
          tc: '去安全區',
        },
      },
    },
    {
      id: 'Thaleia Oschon Wandering Shot North',
      type: 'StartsUsing',
      netRegex: { id: '8CF6', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'South (away from orb)',
          de: 'Süden (weg vom Orb)',
          fr: 'Sud (loin de l\'orbe)',
          cn: '下 (远离球)',
          ko: '남쪽 (구슬에서 멀리)',
          tc: '南 (遠離球)',
        },
      },
    },
    {
      id: 'Thaleia Oschon Wandering Shot South',
      type: 'StartsUsing',
      netRegex: { id: '8CF7', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'North (away from orb)',
          de: 'Norden (weg vom Orb)',
          fr: 'Nord (loin de l\'orbe)',
          cn: '上 (远离球)',
          ko: '북쪽 (구슬에서 멀리)',
          tc: '北 (遠離球)',
        },
      },
    },
    {
      id: 'Thaleia Oschon Wandering Volley North',
      type: 'StartsUsing',
      netRegex: { id: '89AC', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback to south safe spot',
          de: 'Rückstoß zum südlichen sicheren Zone',
          fr: 'Poussée vers la zone sûre au Sud',
          cn: '击退到下方安全区',
          ko: '남쪽 안전지대로 넉백되기',
          tc: '擊退到南方安全區',
        },
      },
    },
    {
      id: 'Thaleia Oschon Wandering Volley South',
      type: 'StartsUsing',
      netRegex: { id: '89AD', source: 'Oschon', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback to north safe spot',
          de: 'Rückstoß zum nördliche sicheren Zone',
          fr: 'Poussée vers la zone sûre au Nord',
          cn: '击退到上方安全区',
          ko: '북쪽 안전지대로 넉백되기',
          tc: '擊退到北方安全區',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Dawn of Time',
      type: 'StartsUsing',
      netRegex: { id: '8A03', source: 'Eulogia', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Thaleia Eulogia Forms',
      type: 'StartsUsing',
      netRegex: { id: Object.keys(eulogiaFormMap), source: 'Eulogia' },
      durationSeconds: (data) => data.eulogiaForms.length < 2 ? 4 : 19,
      infoText: (data, matches, output) => {
        const form = eulogiaFormMap[matches.id.toUpperCase()] ?? 'unknown';
        data.eulogiaForms.push(form);
        if (data.eulogiaForms.length === 3) {
          const [form1, form2, form3] = data.eulogiaForms.map((x) => output[x]!());
          data.eulogiaForms = [];
          return output.text!({ form1: form1, form2: form2, form3: form3 });
        }
        return output[form]!();
      },
      outputStrings: {
        text: {
          en: '${form1} => ${form2} => ${form3}',
          de: '${form1} => ${form2} => ${form3}',
          fr: '${form1} => ${form2} => ${form3}',
          ja: '${form1} => ${form2} => ${form3}',
          cn: '${form1} => ${form2} => ${form3}',
          ko: '${form1} => ${form2} => ${form3}',
          tc: '${form1} => ${form2} => ${form3}',
        },
        left: Outputs.left,
        right: Outputs.right,
        inside: Outputs.in,
        unknown: Outputs.unknown,
      },
    },
    {
      id: 'Thaleia Eulogia the Whorl',
      type: 'StartsUsing',
      netRegex: { id: '8A2F', source: 'Eulogia', capture: false },
      response: Responses.bigAoe(),
    },
    {
      id: 'Thaleia Eulogia Love\'s Light',
      type: 'StartsUsing',
      netRegex: { id: '8A30', source: 'Eulogia', capture: false },
      durationSeconds: 10,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Start from the bright moon',
          de: 'Starte vom hellen Mond',
          fr: 'Commencez depuis la lune pleine',
          ja: '明るい月から',
          cn: '从亮月亮开始',
          ko: '밝은 달부터 시작',
          tc: '從亮月亮開始',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Hydrostasis',
      type: 'StartsUsing',
      netRegex: { id: '8A37', source: 'Eulogia', capture: false },
      durationSeconds: 13,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: '3 => 1 => 2',
          de: '3 => 1 => 2',
          fr: '3 -> 1 -> 2',
          ja: '3 => 1 => 2',
          cn: '3 => 1 => 2',
          ko: '3 => 1 => 2',
          tc: '3 => 1 => 2',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Hieroglyphika',
      type: 'StartsUsing',
      netRegex: { id: '8A43', source: 'Eulogia', capture: false },
      durationSeconds: 20,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go to rotated safe zone',
          de: 'Geh in den sicheren Bereich',
          fr: 'Allez dans une zone sûre après rotation',
          ja: '安置へ移動', // FIXME
          cn: '去旋转后的安全区',
          ko: '회전한 뒤의 안전 지대로 가기',
          tc: '去旋轉後的安全區',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Hand of the Destroyer Red',
      type: 'StartsUsing',
      netRegex: { id: '8A47', source: 'Eulogia', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Be on blue half',
          de: 'Geh zur blauen Seite',
          fr: 'Placez-vous sur la moitié bleue',
          ja: '青い安置',
          cn: '站蓝色半场',
          ko: '파란색 쪽으로',
          tc: '站藍色半場',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Hand of the Destroyer Blue',
      type: 'StartsUsing',
      netRegex: { id: '8A48', source: 'Eulogia', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Be on red half',
          de: 'Geh zur roten Seite',
          fr: 'Placez-vous sur la moitié rouge',
          ja: '赤い安置',
          cn: '站红色半场',
          ko: '빨간색 쪽으로',
          tc: '站紅色半場',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Torrential Tridents',
      type: 'StartsUsing',
      netRegex: { id: '8A4E', source: 'Eulogia', capture: false },
      durationSeconds: 8,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Last trident => 1st trident',
          de: 'Letzer Dreizack => erster Dreizack',
          fr: 'Dernier trident -> 1er trident',
          ja: '最後の槍 => 1番目の槍へ',
          cn: '最后一个三叉戟 => 第一个三叉戟',
          ko: '마지막 창 => 1번째 창으로',
          tc: '最後一個三叉戟 => 第一個三叉戟',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Destructive Bolt',
      type: 'StartsUsing',
      netRegex: { id: '8CFD', source: 'Eulogia' },
      response: Responses.stackMarkerOn(),
    },
    {
      id: 'Thaleia Eulogia Byregot\'s Strike',
      type: 'StartsUsing',
      netRegex: { id: '8A52', source: 'Eulogia', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Knockback (with lightning)',
          de: 'Rückstoß (mit Blitzen)',
          fr: 'Poussée (avec éclair)',
          ja: 'ノックバック (雷)',
          cn: '击退 (带闪电)',
          ko: '넉백 (번개 장판)',
          tc: '擊退 (帶閃電)',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Thousandfold Thrust',
      type: 'HeadMarker',
      netRegex: { id: ['0182', '0183', '0184', '0185'], target: 'Eulogia' },
      alertText: (_data, matches, output) => {
        return {
          '0182': output.back!(),
          '0183': output.front!(),
          '0184': output.left!(),
          '0185': output.right!(),
        }[matches.id];
      },
      outputStrings: {
        front: Outputs.front,
        back: Outputs.back,
        left: Outputs.left,
        right: Outputs.right,
      },
    },
    {
      id: 'Thaleia Eulogia As Above So Below',
      type: 'StartsUsing',
      netRegex: { id: ['8A5B', '8A5C'], source: 'Eulogia' },
      infoText: (_data, matches, output) => {
        if (matches.id === '8A5B')
          return output.red!();
        return output.blue!();
      },
      outputStrings: {
        red: {
          en: 'Blue is safe',
          de: 'Blau ist sicher',
          fr: 'Le bleu est sûr',
          ja: '青安置',
          cn: '蓝色安全',
          ko: '파랑이 안전',
          tc: '藍色安全',
        },
        blue: {
          en: 'Red is safe',
          de: 'Rot ist sicher',
          fr: 'Le rouge est sûr',
          ja: '赤安置',
          cn: '橙色安全',
          ko: '빨강이 안전',
          tc: '紅色安全',
        },
      },
    },
    {
      id: 'Thaleia Eulogia Climbing Shot',
      type: 'StartsUsing',
      netRegex: { id: '8D0B', source: 'Eulogia', capture: false },
      response: Responses.knockback(),
    },
    {
      id: 'Thaleia Eulogia Soaring Minuet',
      type: 'StartsUsing',
      netRegex: { id: '8A69', source: 'Eulogia', capture: false },
      response: Responses.getBehind(),
    },
    {
      id: 'Thaleia Eulogia Eudaimon Eorzea',
      type: 'StartsUsing',
      netRegex: { id: '8A2C', source: 'Eulogia', capture: false },
      durationSeconds: 18,
      response: Responses.aoe(),
    },
  ],
  timelineReplace: [
    {
      'locale': 'en',
      'replaceText': {
        'Blueblossoms/Giltblossoms': 'Blue/Giltblossoms',
        'Left Bank/Right Bank': 'Left/Right Bank',
        'Left Strait/Right Strait': 'Left/Right Strait',
        'Right Bank/Left Bank': 'Right/Left Bank',
      },
    },
    {
      'locale': 'de',
      'replaceSync': {
        'Eulogia': 'Eulogia',
        'Llymlaen(?!\')': 'Llymlaen',
        'Oschon(?!\')': 'Oschon',
        'Oschon\'s Avatar': 'Oschons Abbild',
        'Perykos': 'Perykos',
        'Thalaos': 'Thalaos',
        'Thaliak(?!\')': 'Thaliak',
      },
      'replaceText': {
        'Altitude': 'Höhenlage',
        'Arrow Trail': 'Pfeilschweif',
        'As Above, So Below': 'Flamme von Leben und Tod',
        'Blueblossoms': 'Blaublüten',
        'Byregot\'s Strike': 'Byregots Schlag',
        'Climbing Shot': 'Aufsteigender Schuss',
        'Dawn of Time': 'Himmlischer Blitz',
        'Deep Dive': 'Tiefes Eintauchen',
        'Denizens of the Deep': 'Bewohner der Tiefe',
        'Destructive Bolt': 'Zerstörerischer Blitzschlag',
        'Dire Straits': 'Beidseitige Säuberung',
        'Downhill': 'Regenguss',
        'Eudaimon Eorzea': 'Eudaimon Eorzea',
        'First Blush': 'Lunarer Schuss',
        'First Form': 'Primärhaltung',
        'Flinted Foehn': 'Föhnpfeil',
        'Full Bright': 'Voller Glanz',
        'Giltblossoms': 'Goldblüten',
        'Godsbane': 'Göttliche Geißel',
        'Great Whirlwind': 'Windhose',
        'Hand of the Destroyer': 'Vernichtungsschlag des Zerstörers',
        'Hard Water': 'Reißendes Wasser',
        'Hieroglyphika': 'Hieroglyphen',
        'Hydroptosis': 'Hydroptose',
        'Hydrostasis': 'Hydrostase',
        'Katarraktes': 'Katarakt',
        'Landing': 'Schnelle Landung',
        'Left Bank': 'Linkes Ufer',
        'Left Strait': 'Linkes Fahrwasser',
        'Lightning Bolt': 'Keraunisches Feld',
        'Lofty Peaks': 'Orogenese',
        'Love\'s Light': 'Licht der Liebe',
        'Maelstrom': 'Mahlstrom',
        'Matron\'s Breath': 'Nophicas Atem',
        'Moving Mountains': 'Tektonisches Beben',
        'Navigator\'s Trident': 'Llymlaens Dreizack',
        'Peak Peril': 'Tektonische Turbulenz',
        'Piton Pull': 'Hakengriff',
        'Quintessence': 'Kriegerischer Übermut',
        'Radiant Finish': 'Letzter Fächertanz',
        'Radiant Rhythm': 'Fächertanz',
        'Reproduce': 'Teilung des Selbsts',
        'Rheognosis(?! Petrine)': 'Rheognose',
        'Rheognosis Petrine': 'Rheognose Petros',
        'Rhyton': 'Rhyton',
        'Right Bank': 'Rechtes Ufer',
        'Right Strait': 'Rechtes Fahrwasser',
        'Seafoam Spiral': 'Kreisförmige Welle',
        'Second Form': 'Sekundärhaltung',
        'Serpents\' Tide': 'Schlangenflut',
        'Shockwave': 'Schockwelle',
        'Soaring Minuet': 'Pfeil des Wanderers',
        'Solar Fans': 'Fächerschneide',
        'Stormwhorl': 'Meerestornado',
        'Stormwinds': 'Meerestornado',
        'Stormy Seas': 'Stürmische See',
        'Sudden Downpour': 'Platzregen',
        'Sunbeam': 'Sonnenstrahl',
        'Surging Wave': 'Wellenstoß',
        'Swinging Draw': 'Schweifender Pfeil',
        'Tempest': 'Schwerer Sturm',
        'Tetraktuos Kosmos': 'Tetraktuos Kosmos',
        'Tetraktys': 'Tetraktuos',
        'The Arrow': 'Oschons Pfeil',
        'The Builder\'s Art': 'Byregots göttliche Macht',
        'The Destroyer\'s Might': 'Rhalghrs göttliche Macht',
        'The Fury\'s Ambition': 'Halones göttliche Macht',
        'The Keeper\'s Gravity': 'Althyks göttliche Macht',
        'The Lover\'s Devotion': 'Menphinas göttliche Macht',
        'The Matron\'s Plenty': 'Nophicas göttliche Macht',
        'The Navigator\'s Command': 'Llymlaens göttliche Macht',
        'The Scholar\'s Wisdom': 'Thaliaks göttliche Macht',
        'The Spinner\'s Cunning': 'Nymeias göttliche Macht',
        'The Traders\' Equity': 'Nald\'thals göttliche Macht',
        'The Wanderer\'s Whimsy': 'Oschons göttliche Macht',
        'The Warden\'s Radiance': 'Azeymas göttliche Macht',
        'The Whorl': 'Göttlicher Schöpfungswirbel',
        'Third Form': 'Tertiärhaltung',
        'Thlipsis': 'Drangsal',
        'Thousandfold Thrust': 'Tausendfacher Stoß',
        'Time and Tide': 'Zeit und Gezeiten',
        'To the Last': 'Endlose Wellen',
        'Torrential Tridents': 'Himmlische Harpunen',
        'Trek Shot': 'Langsamer Pfeil',
        'Wandering Shot': 'Umherschweifender Schuss',
        'Wandering Volley': 'Streifschüsse',
        'Wind Rose': 'Kreisförmiger Wirbel',
      },
    },
    {
      'locale': 'fr',
      'replaceSync': {
        'Eulogia': 'Eulogie',
        'Llymlaen(?!\')': 'Llymlaen',
        'Oschon(?!\')': 'Oschon',
        'Oschon\'s Avatar': 'double d\'Oschon',
        'Perykos': 'Pérykos',
        'Thalaos': 'Thalaos',
        'Thaliak(?!\')': 'Thaliak',
      },
      'replaceText': {
        'Altitude': 'Altitude',
        'Arrow Trail': 'Traînées de flèches',
        'As Above, So Below': 'Flamme de vie, flamme de mort',
        'Blueblossoms': 'Pétales d\'azur',
        'Byregot\'s Strike': 'Foreuse de Byregot',
        'Climbing Shot': 'Tir ascendant',
        'Dawn of Time': 'Éclair céleste',
        'Deep Dive': 'Noyade abyssale',
        'Denizens of the Deep': 'Appel des serpents marins',
        'Destructive Bolt': 'Foudre destructrice',
        'Dire Straits': 'Éradication totale',
        'Downhill': 'Averse pénétrante',
        'Eudaimon Eorzea': 'Eudémon éorzéen',
        'First Blush': 'Scintillement sélénien',
        'First Form': 'Posture primaire',
        'Flinted Foehn': 'Flèches de foehn',
        'Full Bright': 'Nuit de pleine lune',
        'Giltblossoms': 'Pétales dorés',
        'Godsbane': 'Fléau divin',
        'Great Whirlwind': 'Grand tourbillon',
        'Hand of the Destroyer': 'Main du Destructeur',
        'Hard Water': 'Oppression aqueuse',
        'Hieroglyphika': 'Hiéroglyphe',
        'Hydroptosis': 'Hydroptôse',
        'Hydrostasis': 'Hydrostase',
        'Katarraktes': 'Cataracte',
        'Landing': 'Atterrissage rapide',
        'Left Bank': 'Débordement rive gauche',
        'Left Strait': 'Éradication à bâbord',
        'Lightning Bolt': 'Éclair de foudre',
        'Lofty Peaks': 'Orogenèse',
        'Love\'s Light': 'Brillance de l\'amour',
        'Maelstrom': 'Maelström',
        'Matron\'s Breath': 'Souffle de la Mère',
        'Moving Mountains': 'Secousse tectonique',
        'Navigator\'s Trident': 'Trident de Llymlaen',
        'Peak Peril': 'Turbulence tectonique',
        'Piton Pull': 'Arrachement de pitons',
        'Quintessence': 'Exaltation guerrière',
        'Radiant Finish': 'Final enflammé',
        'Radiant Rhythm': 'Rythme enflammé',
        'Reproduce': 'Clonage',
        'Rheognosis(?! Petrine)': 'Leonosis',
        'Rheognosis Petrine': 'Leonosis petros',
        'Rhyton': 'Rhyton',
        'Right Bank': 'Débordement rive droite',
        'Right Strait': 'Éradication à tribord',
        'Seafoam Spiral': 'Vague circulaire',
        'Second Form': 'Posture secondaire',
        'Serpents\' Tide': 'Ruée serpentine',
        'Shockwave': 'Onde de choc',
        'Soaring Minuet': 'Flèche du menuet',
        'Solar Fans': 'Éventails solaires',
        'Stormwhorl': 'Tornade marine',
        'Stormwinds': 'Brise marine',
        'Stormy Seas': 'Déchaînement marin',
        'Sudden Downpour': 'Déluge dru',
        'Sunbeam': 'Rayon de soleil',
        'Surging Wave': 'Transperce-vague',
        'Swinging Draw': 'Tir oscillant',
        'Tempest': 'Grande tempête',
        'Tetraktuos Kosmos': 'Tetractys cosmos',
        'Tetraktys': 'Tetractys',
        'The Arrow': 'Flèche d\'Oschon',
        'The Builder\'s Art': 'Pouvoir divin de Byregot',
        'The Destroyer\'s Might': 'Pouvoir divin de Rhalgr',
        'The Fury\'s Ambition': 'Pouvoir divin de Halone',
        'The Keeper\'s Gravity': 'Pouvoir divin d\'Althyk',
        'The Lover\'s Devotion': 'Pouvoir divin de Menphina',
        'The Matron\'s Plenty': 'Pouvoir divin de Nophica',
        'The Navigator\'s Command': 'Pouvoir divin de Llymlaen',
        'The Scholar\'s Wisdom': 'Pouvoir divin de Thaliak',
        'The Spinner\'s Cunning': 'Pouvoir divin de Nymeia',
        'The Traders\' Equity': 'Pouvoir divin de Nald\'thal',
        'The Wanderer\'s Whimsy': 'Pouvoir divin d\'Oschon',
        'The Warden\'s Radiance': 'Pouvoir divin d\'Azeyma',
        'The Whorl': 'Vortex génésiaque',
        'Third Form': 'Posture tertiaire',
        'Thlipsis': 'Tribulation',
        'Thousandfold Thrust': 'Transpercement millénaire',
        'Time and Tide': 'Manipulation temporelle',
        'To the Last': 'Vagues infinies',
        'Torrential Tridents': 'Pluie de tridents',
        'Trek Shot': 'Flèches languissantes',
        'Wandering Shot': 'Tir errant',
        'Wandering Volley': 'Tir de barrage errant',
        'Wind Rose': 'Bourrasque circulaire',
      },
    },
    {
      'locale': 'ja',
      'replaceSync': {
        'Eulogia': 'エウロギア',
        'Llymlaen(?!\')': 'リムレーン',
        'Oschon(?!\')': 'オシュオン',
        'Oschon\'s Avatar': 'オシュオンの分体',
        'Perykos': 'ペリュコス',
        'Thalaos': 'サラオス',
        'Thaliak(?!\')': 'サリャク',
      },
      'replaceText': {
        'Altitude': 'アルティチュード',
        'Arrow Trail': 'アロートレイル',
        'As Above, So Below': '死生択一の炎',
        'Blueblossoms': '青花散',
        'Byregot\'s Strike': 'ビエルゴズ・ストライク',
        'Climbing Shot': 'クライムシュート',
        'Dawn of Time': '星天極光',
        'Deep Dive': '海淵落とし',
        'Denizens of the Deep': '海蛇招来',
        'Destructive Bolt': '壊雷撃',
        'Dire Straits': '両舷掃討',
        'Downhill': 'ダウンヒル',
        'Eudaimon Eorzea': 'エウダイモン・エオルゼア',
        'First Blush': '月閃',
        'First Form': '壱の構え',
        'Flinted Foehn': 'フェーンアロー',
        'Full Bright': '月夜の巡り',
        'Giltblossoms': '黄花散',
        'Godsbane': 'ゴッズベーン',
        'Great Whirlwind': '大旋風',
        'Hand of the Destroyer': '壊神創幻撃',
        'Hard Water': '重水塊',
        'Hieroglyphika': 'ヒエログリュフィカ',
        'Hydroptosis': 'ヒュドルピトシス',
        'Hydrostasis': 'ヒュドルスタシス',
        'Katarraktes': 'カタラクティス',
        'Landing': '落着',
        'Left Bank': '左岸氾濫',
        'Left Strait': '左舷掃討',
        'Lightning Bolt': '落雷',
        'Lofty Peaks': '風天の霊峰',
        'Love\'s Light': '慈愛の月',
        'Maelstrom': 'メイルシュトローム',
        'Matron\'s Breath': '豊穣の息吹',
        'Moving Mountains': '造山振動',
        'Navigator\'s Trident': 'リムレーンズトライデント',
        'Peak Peril': '造山乱流',
        'Piton Pull': 'ハーケンブリング',
        'Quintessence': '武気発揚',
        'Radiant Finish': '焔扇流舞・終炎',
        'Radiant Rhythm': '焔扇流舞',
        'Reproduce': '分体生成',
        'Rheognosis(?! Petrine)': 'レーオノシス',
        'Rheognosis Petrine': 'レーオノシス・ペトゥロス',
        'Rhyton': 'リュトン',
        'Right Bank': '右岸氾濫',
        'Right Strait': '右舷掃討',
        'Seafoam Spiral': '輪の波浪',
        'Second Form': '弐の構え',
        'Serpents\' Tide': '海蛇の進撃',
        'Shockwave': '衝撃波',
        'Soaring Minuet': 'メヌエットアロー',
        'Solar Fans': '紅炎扇刃',
        'Stormwhorl': '時化の渦風',
        'Stormwinds': '時化の潮風',
        'Stormy Seas': '大時化起こし',
        'Sudden Downpour': 'ダウンポール',
        'Sunbeam': '太陽光',
        'Surging Wave': '波穿ち',
        'Swinging Draw': 'スイングアロー',
        'Tempest': '大海嵐',
        'Tetraktuos Kosmos': 'テトラクテュス・コスモス',
        'Tetraktys': 'テトラクテュス',
        'The Arrow': 'オシュオンの矢',
        'The Builder\'s Art': 'ビエルゴの神力',
        'The Destroyer\'s Might': 'ラールガーの神力',
        'The Fury\'s Ambition': 'ハルオーネの神力',
        'The Keeper\'s Gravity': 'アルジクの神力',
        'The Lover\'s Devotion': 'メネフィナの神力',
        'The Matron\'s Plenty': 'ノフィカの神力',
        'The Navigator\'s Command': 'リムレーンの神力',
        'The Scholar\'s Wisdom': 'サリャクの神力',
        'The Spinner\'s Cunning': 'ニメーヤの神力',
        'The Traders\' Equity': 'ナルザルの神力',
        'The Wanderer\'s Whimsy': 'オシュオンの神力',
        'The Warden\'s Radiance': 'アーゼマの神力',
        'The Whorl': '創世の神渦',
        'Third Form': '参の構え',
        'Thlipsis': 'スリプシス',
        'Thousandfold Thrust': 'サウザンスラスト',
        'Time and Tide': '時間操作',
        'To the Last': '千波万波',
        'Torrential Tridents': '銛の雨',
        'Trek Shot': 'トレックアロー',
        'Wandering Shot': 'ワンダリングショット',
        'Wandering Volley': 'ワンダリングバラージ',
        'Wind Rose': '円の旋風',
      },
    },
    {
      'locale': 'cn',
      'replaceSync': {
        'Eulogia': '欧罗基亚',
        'Llymlaen(?!\')': '利姆莱茵',
        'Oschon(?!\')': '奥修昂',
        'Oschon\'s Avatar': '奥修昂的分身',
        'Perykos': '珀琉喀斯',
        'Thalaos': '撒拉奥斯',
        'Thaliak(?!\')': '沙利亚克',
      },
      'replaceText': {
        'Altitude': '高空箭雨',
        'Arrow Trail': '箭踪',
        'As Above, So Below': '生死抉择之炎',
        'Blueblossoms': '蓝花飞散',
        'Byregot\'s Strike': '工艺神击',
        'Climbing Shot': '攀跃射击',
        'Dawn of Time': '星天极光',
        'Deep Dive': '坠海渊',
        'Denizens of the Deep': '召唤海蛇',
        'Destructive Bolt': '坏灭雷击',
        'Dire Straits': '两舷横扫',
        'Downhill': '落山箭',
        'Eudaimon Eorzea': '福润艾欧泽亚',
        'First Blush': '月闪',
        'First Form': '壹之体势',
        'Flinted Foehn': '焚风箭',
        'Full Bright': '月夜巡游',
        'Giltblossoms': '黄花飞散',
        'Godsbane': '神祸',
        'Great Whirlwind': '大龙卷风',
        'Hand of the Destroyer': '幻天破坏拳',
        'Hard Water': '重水块',
        'Hieroglyphika': '圣书体',
        'Hydroptosis': '水之倾泻',
        'Hydrostasis': '水之停滞',
        'Katarraktes': '飞瀑',
        'Landing': '落地',
        'Left Bank': '左岸泛滥',
        'Left Strait': '左舷横扫',
        'Lightning Bolt': '落雷',
        'Lofty Peaks': '风天灵峰',
        'Love\'s Light': '慈爱的月亮',
        'Maelstrom': '巨旋涡',
        'Matron\'s Breath': '丰饶的吐息',
        'Moving Mountains': '造山震动',
        'Navigator\'s Trident': '海洋女神的尖枪',
        'Peak Peril': '造山乱流',
        'Piton Pull': '拔拽绳钩',
        'Quintessence': '武意高昂',
        'Radiant Finish': '炎扇流舞·终炎',
        'Radiant Rhythm': '炎扇流舞',
        'Reproduce': '生成分裂体',
        'Rheognosis(?! Petrine)': '知识的激流',
        'Rheognosis Petrine': '知识的激流·滚岩',
        'Rhyton': '流动',
        'Right Bank': '右岸泛滥',
        'Right Strait': '右舷横扫',
        'Seafoam Spiral': '海浪回环',
        'Second Form': '贰之体势',
        'Serpents\' Tide': '海蛇突进',
        'Shockwave': '冲击波',
        'Soaring Minuet': '小步舞箭',
        'Solar Fans': '红炎扇刃',
        'Stormwhorl': '暴雨涡风',
        'Stormwinds': '暴雨潮风',
        'Stormy Seas': '召唤大暴风雨',
        'Sudden Downpour': '暴雨箭',
        'Sunbeam': '太阳光',
        'Surging Wave': '破浪',
        'Swinging Draw': '摇荡射击',
        'Tempest': '大暴风',
        'Tetraktuos Kosmos': '圣十结构·秩序',
        'Tetraktys': '圣十结构',
        'The Arrow': '放浪神之箭',
        'The Builder\'s Art': '比尔格之技艺',
        'The Destroyer\'s Might': '拉尔戈之威能',
        'The Fury\'s Ambition': '哈罗妮之壮志',
        'The Keeper\'s Gravity': '阿尔基克之重力',
        'The Lover\'s Devotion': '梅茵菲娜之慈爱',
        'The Matron\'s Plenty': '诺菲卡之丰饶',
        'The Navigator\'s Command': '莉姆莱茵之敕令',
        'The Scholar\'s Wisdom': '沙利亚克之灵慧',
        'The Spinner\'s Cunning': '妮美雅之精巧',
        'The Traders\' Equity': '纳尔札尔之公正',
        'The Wanderer\'s Whimsy': '奥修昂之离奇',
        'The Warden\'s Radiance': '阿泽玛之光辉',
        'The Whorl': '创世神涡',
        'Third Form': '叁之体势',
        'Thlipsis': '磨难',
        'Thousandfold Thrust': '千枪连刺',
        'Time and Tide': '时间操纵',
        'To the Last': '怒涛滚滚',
        'Torrential Tridents': '尖枪雨',
        'Trek Shot': '撤步射击',
        'Wandering Shot': '放浪射击',
        'Wandering Volley': '放浪群射',
        'Wind Rose': '狂风旋圆',
      },
    },
    {
      'locale': 'tc',
      'replaceSync': {
        'Eulogia': '歐羅基亞',
        'Llymlaen(?!\')': '利姆萊茵',
        'Oschon(?!\')': '奧修昂',
        'Oschon\'s Avatar': '奧修昂的分身',
        'Perykos': '珀琉喀斯',
        'Thalaos': '撒拉奧斯',
        'Thaliak(?!\')': '沙利亞克',
      },
      'replaceText': {
        'Altitude': '高空箭雨',
        'Arrow Trail': '箭蹤',
        'As Above, So Below': '生死抉擇之炎',
        'Blueblossoms': '藍花飛散',
        'Byregot\'s Strike': '工藝神擊',
        'Climbing Shot': '攀躍射擊',
        'Dawn of Time': '星天極光',
        'Deep Dive': '墜海淵',
        'Denizens of the Deep': '召喚海蛇',
        'Destructive Bolt': '壞滅雷擊',
        'Dire Straits': '兩舷橫掃',
        'Downhill': '落山箭',
        'Eudaimon Eorzea': '福潤艾奧傑亞',
        'First Blush': '月閃',
        'First Form': '壹之體勢',
        'Flinted Foehn': '焚風箭',
        'Full Bright': '月夜巡遊',
        'Giltblossoms': '黃花飛散',
        'Godsbane': '神禍',
        'Great Whirlwind': '大旋風',
        'Hand of the Destroyer': '幻天破壞拳',
        'Hard Water': '重水塊',
        'Hieroglyphika': '聖書體',
        'Hydroptosis': '水之傾瀉',
        'Hydrostasis': '水之停滯',
        'Katarraktes': '飛瀑',
        'Landing': '落地',
        'Left Bank': '左岸氾濫',
        'Left Strait': '左舷橫掃',
        'Lightning Bolt': '落雷',
        'Lofty Peaks': '風天靈峰',
        'Love\'s Light': '慈愛的月亮',
        'Maelstrom': '巨旋渦',
        'Matron\'s Breath': '豐饒的吐息',
        'Moving Mountains': '造山震動',
        'Navigator\'s Trident': '海之女神的尖槍',
        'Peak Peril': '造山亂流',
        'Piton Pull': '拔拽繩鉤',
        'Quintessence': '武意高昂',
        'Radiant Finish': '炎扇流舞·終炎',
        'Radiant Rhythm': '炎扇流舞',
        'Reproduce': '生成分裂體',
        'Rheognosis(?! Petrine)': '知識的激流',
        'Rheognosis Petrine': '知識的激流·滾岩',
        'Rhyton': '流動',
        'Right Bank': '右岸氾濫',
        'Right Strait': '右舷橫掃',
        'Seafoam Spiral': '海浪回環',
        'Second Form': '貳之體勢',
        'Serpents\' Tide': '海蛇突進',
        'Shockwave': '衝擊波',
        'Soaring Minuet': '小步舞箭',
        'Solar Fans': '紅炎扇刃',
        'Stormwhorl': '暴雨渦風',
        'Stormwinds': '暴雨潮風',
        'Stormy Seas': '召喚大暴風雨',
        'Sudden Downpour': '暴雨箭',
        'Sunbeam': '太陽光',
        'Surging Wave': '破浪',
        'Swinging Draw': '搖盪射擊',
        'Tempest': '大暴風',
        'Tetraktuos Kosmos': '聖十結構·秩序',
        'Tetraktys': '聖十結構',
        'The Arrow': '放浪神之箭',
        'The Builder\'s Art': '比爾格之技藝',
        'The Destroyer\'s Might': '拉爾戈之威能',
        'The Fury\'s Ambition': '哈羅妮之壯志',
        'The Keeper\'s Gravity': '阿爾基克之重力',
        'The Lover\'s Devotion': '梅茵菲娜之慈愛',
        'The Matron\'s Plenty': '諾菲卡之豐饒',
        'The Navigator\'s Command': '利姆萊茵之敕令',
        'The Scholar\'s Wisdom': '沙利亞克之靈慧',
        'The Spinner\'s Cunning': '妮美雅之精巧',
        'The Traders\' Equity': '納爾札爾之公正',
        'The Wanderer\'s Whimsy': '奧修昂之離奇',
        'The Warden\'s Radiance': '阿澤瑪之光輝',
        'The Whorl': '創世神渦',
        'Third Form': '三之體勢',
        'Thlipsis': '磨難',
        'Thousandfold Thrust': '千槍連刺',
        'Time and Tide': '時間操縱',
        'To the Last': '怒濤滾滾',
        'Torrential Tridents': '尖槍雨',
        'Trek Shot': '撤步射擊',
        'Wandering Shot': '放浪射擊',
        'Wandering Volley': '放浪群射',
        'Wind Rose': '狂風旋圓',
      },
    },
    {
      'locale': 'ko',
      'replaceSync': {
        'Eulogia': '에울로기아',
        'Llymlaen(?!\')': '리믈렌',
        'Oschon(?!\')': '오쉬온',
        'Oschon\'s Avatar': '오쉬온의 분신',
        'Perykos': '페뤼코스',
        'Thalaos': '살라오스의 유해',
        'Thaliak(?!\')': '살리아크',
      },
      'replaceText': {
        '\\(cast\\)': '(시전)',
        'Altitude': '해발고도',
        'Arrow Trail': '화살 둘레길',
        'As Above, So Below': '생사택일의 화염',
        'Blueblossoms': '푸른꽃 만발',
        'Byregot\'s Strike': '비레고의 강타',
        'Climbing Shot': '등반 사격',
        'Dawn of Time': '성천극광',
        'Deep Dive': '해연 강하',
        'Denizens of the Deep': '바다뱀 소환',
        'Destructive Bolt': '파괴번개 일격',
        'Dire Straits': '양현 소탕',
        'Downhill': '내리막',
        'Eudaimon Eorzea': '에우다이몬 에오르제아',
        'First Blush': '달의 섬광',
        'First Form': '첫 번째 태세',
        'Flinted Foehn': '높새바람 화살',
        'Full Bright': '환한 달밤',
        'Giltblossoms': '노란꽃 만발',
        'Godsbane': '신의 재앙',
        'Great Whirlwind': '대선풍',
        'Hand of the Destroyer': '파괴신 창환격',
        'Hard Water': '무거운 물덩어리',
        'Hieroglyphika': '신성문자',
        'Hydroptosis': '쏟아지는 물',
        'Hydrostasis': '우뚝 선 물',
        'Katarraktes': '홍수',
        'Landing': '발착장',
        'Left Bank': '좌측 범람',
        'Left Strait': '좌현 소탕',
        'Lightning Bolt': '낙뢰',
        'Lofty Peaks': '풍천의 영봉',
        'Love\'s Light': '자애로운 달',
        'Maelstrom': '흑와 투기장',
        'Matron\'s Breath': '풍요의 숨결',
        'Moving Mountains': '조산 진동',
        'Navigator\'s Trident': '리믈렌의 삼지창',
        'Peak Peril': '조산 난류',
        'Piton Pull': '갈고리 당기기',
        'Quintessence': '투기 발양',
        'Radiant Finish': '불부채춤 마무리',
        'Radiant Rhythm': '불부채춤',
        'Reproduce': '분신 생성',
        'Rheognosis(?! Petrine)': '지식의 격류',
        'Rheognosis Petrine': '지식의 격류: 물바위',
        'Rhyton': '유동',
        'Right Bank': '우측 범람',
        'Right Strait': '우현 소탕',
        'Seafoam Spiral': '외곽 물너울',
        'Second Form': '두 번째 태세',
        'Serpents\' Tide': '바다뱀 진격',
        'Shockwave': '충격파',
        'Soaring Minuet': '무곡 화살',
        'Solar Fans': '홍염 부채칼',
        'Stormwhorl': '풍파의 회오리바람',
        'Stormwinds': '풍파의 바닷바람',
        'Stormy Seas': '대풍파 일으키기',
        'Sudden Downpour': '화살 폭우',
        'Sunbeam': '태양광',
        'Surging Wave': '파도 꿰뚫기',
        'Swinging Draw': '순회 화살',
        'Tempest': '대해풍',
        'Tetraktuos Kosmos': '테트락티스 코스모스',
        'Tetraktys': '테트락티스',
        'The Arrow': '오쉬온의 화살',
        'The Builder\'s Art': '비레고의 신력',
        'The Destroyer\'s Might': '랄거의 신력',
        'The Fury\'s Ambition': '할로네의 신력',
        'The Keeper\'s Gravity': '알디크의 신력',
        'The Lover\'s Devotion': '메느피나의 신력',
        'The Matron\'s Plenty': '노피카의 신력',
        'The Navigator\'s Command': '리믈렌의 신력',
        'The Scholar\'s Wisdom': '살리아크의 신력',
        'The Spinner\'s Cunning': '니메이아의 신력',
        'The Traders\' Equity': '날달의 신력',
        'The Wanderer\'s Whimsy': '오쉬온의 신력',
        'The Warden\'s Radiance': '아제마의 신력',
        'The Whorl': '창세의 소용돌이',
        'Third Form': '세 번째 태세',
        'Thlipsis': '고난',
        'Thousandfold Thrust': '천 갈래 찌르기',
        'Time and Tide': '시간 조작',
        'To the Last': '천파만파',
        'Torrential Tridents': '작살비',
        'Trek Shot': '유람 화살',
        'Wandering Shot': '방랑 사격',
        'Wandering Volley': '방랑 일제 사격',
        'Wind Rose': '내곽 회오리',
      },
    },
  ],
};

export default triggerSet;
